"use client"
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useEffect, useState } from 'react';
import { supabase } from '@/utils/supabase/client';
import { useAuth } from '@/contexts/auth-context';
import { Loader2 } from 'lucide-react';
import { format, subDays } from 'date-fns';

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#AF19FF"];

interface DashboardData {
    todaysAppointments: { count: number, yesterday: number };
    todaysRevenue: { amount: number, yesterday: number };
    newPatients: { count: number, yesterday: number };
    admissions: { active: number, dischargesToday: number };
    revenueVsExpenses: { name: string, Revenue: number, Expenses: number }[];
    appointmentsBySpecialty: { name: string, value: number }[];
    appointmentsByStatus: { name: string, value: number }[];
    newPatientTrend: { date: string, value: number }[];
}

function calculatePercentageChange(current: number, previous: number) {
    if (previous === 0) {
        return current > 0 ? 100 : 0;
    }
    return ((current - previous) / previous) * 100;
}

export default function DashboardPage() {
    const { currentOrganization } = useAuth();
    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);

  useEffect(() => {
        const fetchData = async () => {
            if (!currentOrganization) return;
            setLoading(true);
            try {
                const { data, error } = await supabase.rpc('get_main_dashboard_data', {
                    org_id: currentOrganization.id
                });
                if (error) throw new Error(error.message);
                setData(data);
            } catch (error) {
                console.error("Error fetching dashboard data:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [currentOrganization]);

    if (loading) {
        return <div className="flex justify-center items-center h-screen"><Loader2 className="w-16 h-16 animate-spin" /></div>;
    }

    if (!data) {
        return <div className="text-center py-10">Could not load dashboard data.</div>;
    }
    
    const {
        todaysAppointments,
        todaysRevenue,
        newPatients,
        admissions,
      revenueVsExpenses,
      appointmentsBySpecialty,
        appointmentsByStatus,
        newPatientTrend
    } = data;
    
    const appointmentChange = calculatePercentageChange(todaysAppointments.count, todaysAppointments.yesterday);
    const revenueChange = calculatePercentageChange(todaysRevenue.amount, todaysRevenue.yesterday);
    const newPatientsChange = calculatePercentageChange(newPatients.count, newPatients.yesterday);

    return (
        <div className="flex-1 space-y-4 p-8 pt-6">
            <h2 className="text-3xl font-bold tracking-tight">Welcome back, Doctor!</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Today's Appointments</CardTitle>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" className="h-4 w-4 text-muted-foreground"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"></rect><line x1="16" x2="16" y1="2" y2="6"></line><line x1="8" x2="8" y1="2" y2="6"></line><line x1="3" x2="21" y1="10" y2="10"></line></svg>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{todaysAppointments.count}</div>
                        <p className={`text-xs ${appointmentChange >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                           {appointmentChange.toFixed(1)}% from yesterday
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Today's Revenue</CardTitle>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" className="h-4 w-4 text-muted-foreground"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">Rs. {todaysRevenue.amount.toLocaleString()}</div>
                        <p className={`text-xs ${revenueChange >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {revenueChange.toFixed(1)}% from yesterday
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">New Patients Today</CardTitle>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" className="h-4 w-4 text-muted-foreground"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><line x1="19" x2="19" y1="8" y2="14"></line><line x1="22" x2="16" y1="11" y2="11"></line></svg>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">+{newPatients.count}</div>
                        <p className={`text-xs ${newPatientsChange >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                           {newPatientsChange.toFixed(1)}% from yesterday
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Active Admissions</CardTitle>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" className="h-4 w-4 text-muted-foreground"><path d="M22 12h-4l-3 9L9 3l-3 9H2"></path></svg>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{admissions.active}</div>
                        <p className="text-xs text-muted-foreground">
                            {admissions.dischargesToday > 0 ? `${admissions.dischargesToday} discharges today` : 'No discharges today'}
                        </p>
                    </CardContent>
                </Card>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
                <Card className="col-span-4">
                    <CardHeader>
                        <CardTitle>Revenue vs Expenses</CardTitle>
                        <CardDescription>Monthly comparison for the last 6 months</CardDescription>
                    </CardHeader>
                    <CardContent className="pl-2">
                        <ResponsiveContainer width="100%" height={350}>
                             <BarChart data={revenueVsExpenses}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="Revenue" fill="#82ca9d" />
                                <Bar dataKey="Expenses" fill="#ff7300" />
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
                <Card className="col-span-3">
                    <CardHeader>
                        <CardTitle>Appointments by Specialty</CardTitle>
                        <CardDescription>Current month distribution</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={350}>
                            <PieChart>
                                <Pie data={appointmentsBySpecialty} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                                    {appointmentsBySpecialty.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
                <Card className="col-span-3">
                    <CardHeader>
                        <CardTitle>Appointments by Status</CardTitle>
                        <CardDescription>Current month status distribution</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={350}>
                            <PieChart>
                                <Pie data={appointmentsByStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                                    {appointmentsByStatus.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
                <Card className="col-span-4">
                    <CardHeader>
                        <CardTitle>New Patient Trend</CardTitle>
                        <CardDescription>New patient registrations over last 30 days</CardDescription>
                    </CardHeader>
                    <CardContent className="pl-2">
                        <ResponsiveContainer width="100%" height={350}>
                             <LineChart data={newPatientTrend}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" tickFormatter={(str) => format(new Date(str), "MMM d")} />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="value" name="New Patients" stroke="#8884d8" />
                            </LineChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
